/* eslint-disable max-lines */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import moment from 'moment'
import _ from 'underscore'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'

import AddButton from 'components/AddButton'
import Checkbox from 'components/Checkbox'
import Table from 'components/Table'

import SiteMessage from 'pages/shared/SiteMessage'

import Brand from 'helpers/brand'
import { shortDate } from 'helpers/formatters'

import useSiteMessages from 'hooks/useSiteMessages'
import { useStoreContext } from 'hooks/StoreContext'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize, LoadingSinglePanePage } from 'styles/shared'

const SiteMessages = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now())

  useSiteMessages({ listForAllBrands: true, listForAllTiers: true, getPublishedOnly: false, refreshTimestamp })

  const navigate = useNavigate()

  const { siteMessages, areSiteMessagesLoaded } = useStoreContext()

  const [showArchived, setShowArchived] = useState(false)

  if (!areSiteMessagesLoaded) {
    return <LoadingSinglePanePage />
  }

  const sortedSiteMessages = () => (
    _.chain(siteMessages)
      .filter((siteMessage) => !siteMessage.archivedAt || showArchived)
      .sortBy(siteMessage => {
        let sortValue = moment(siteMessage.publishedAt || siteMessage.updatedAt).toDate().getTime()

        if (siteMessage.publishedAt) { sortValue = sortValue * 10 }
        if (siteMessage.siteBannerEnabled) { sortValue = sortValue * 100 }
        if (!siteMessage.archivedAt) { sortValue = sortValue * 100 }

        return sortValue
      })
      .reverse()
      .value()
  )

  const onClickArchive = (siteMessageId) => {
    Api.SiteMessages.archive(siteMessageId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  const onClickRestore = (siteMessageId) => {
    Api.SiteMessages.restore(siteMessageId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  return (
    <>
      <S.Header>
        <S.StatusBanner>
          <S.InfoStatus messageType="info">Informational</S.InfoStatus>
          <S.InfoStatus messageType="warning">Warning</S.InfoStatus>
        </S.StatusBanner>
        <S.Checkbox
          label="Show archived messages"
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
        />
        <AddButton uri="/admin/site_messages/new" testId="Add Message">Add Message</AddButton>
      </S.Header>
      <Table
        items={siteMessages}
        noDataMessage={{
          title: 'No Site Messages',
          description: 'Click "Add Message" to add your first site message.'
        }}
      >
        <S.TableHeader>
          <S.TableHeaderCell title="Message" flexValue="4" />
          <S.TableHeaderCell title="Role" flexValue="2.5" />
          <S.TableHeaderCell title="Site Banner" />
          <S.TableHeaderCell title="Updates" />
          <S.TableHeaderCell title="Date" />
        </S.TableHeader>

        {sortedSiteMessages().map((siteMessage) => (
          <S.Row key={siteMessage.id}>
            <S.MessageCell flexValue="4">
              <S.InfoStatus messageType={siteMessage.messageType} noText />
              <S.SiteMessage>
                <SiteMessage.Body
                    showCollapsed
                    siteMessage={siteMessage}
                />
              </S.SiteMessage>
            </S.MessageCell>
            <S.MessageCell flexValue="2.5">
              <S.RolesAndBrands>
                {siteMessage.roles.map((role) => (
                  <S.Role key={role} isFsr={role === 'fsr'}>{role}</S.Role>
                ))}
                {
                  siteMessage.brands.map(brand => {
                    const productName = Brand.productName(brand)
                    const logoSrc = Brand.brandLogoWithOutText(brand)
                    return (
                      <S.ProductLogo
                      key={productName}
                      src={logoSrc}
                      aria-label="product-logo"
                      alt={`${productName} logo`}
                      />
                    )
                  })
                }
              </S.RolesAndBrands>
            </S.MessageCell>
            <S.MessageCell>
              {siteMessage.siteBannerEnabled ? 'Enabled' : 'Disabled'}
            </S.MessageCell>
            <S.MessageCell>
              <S.Status enabled={siteMessage.published}>
                {siteMessage.published ? 'Published' : 'Unpublished'}
              </S.Status>
            </S.MessageCell>
            <S.MessageCell>
              <S.DateWrapper publishedAt={siteMessage.publishedAt}>
                {siteMessage.publishedAt && <div>{shortDate(siteMessage.publishedAt)}</div>}
                <S.Actions>
                  <S.EditIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/site_messages/${siteMessage.id}/edit`)}
                    aria-label={`Edit Site Message with title: ${siteMessage.title}`}
                  >
                    Edit
                  </S.EditIcon>
                  { !siteMessage.archivedAt
                    ? <S.ArchiveOutlinedIcon
                        role="img"
                        aria-label="Archive Message"
                        onClick={() => onClickArchive(siteMessage.id)}
                      />
                    : <S.RestoreOutlinedIcon role="img" aria-label="Restore Message" onClick={() => onClickRestore(siteMessage.id)} />
                  }
                </S.Actions>
              </S.DateWrapper>
            </S.MessageCell>
          </S.Row>
        ))}
      </Table>
    </>
  )
}

export default SiteMessages

const iconStyle = (iconType) => {
  return css`
    width: 1.2em;
    height: 1.2em;
    cursor: pointer;
    color: ${color(iconType)};
    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const MESSAGE_COLUMN_FLEX_VALUE = '1'

const S = {
  Header: styled.div`
    display: flex;
    gap: 30px;
    justify-content: end;
    margin: 15px;
  `,
  StatusBanner: styled.div`
    display: flex;
    gap: 15px;
  `,
  InfoStatus: styled.div.withConfig({
    shouldForwardProp: (prop) => !['messageType', 'noText'].includes(prop)
  })`
    font-size: 13px;
    &::after {
      content: ' ';
      display: inline-block;
      width: 12px;
      height: 12px;
      position: relative;
      border-radius: 50%;
      ${({ noText }) => !noText && `
        top: 2px;
        left: 5px;
      `}
      background-color: ${({ messageType }) => (messageType === 'info'
    ? color('alertLookupButtonHover')
    : color('adminBannerStaging')
  )};
    }
  `,
  Checkbox: styled(Checkbox)`
    text-transform: capitalize;
    font-weight: 600;
    color: ${color('pageTitle')};
  `,
  TableHeaderCell: styled(Table.HeaderCell).withConfig({
    shouldForwardProp: (prop) => prop !== 'flexValue'
  })`
    background-color: ${color('customerPinUnpinned')};
    flex: ${({ flexValue }) => flexValue ?? MESSAGE_COLUMN_FLEX_VALUE};
    border-bottom-left-radius: 0 !important;
    
    &:last-child {
      border-bottom-right-radius: 0 !important;
    }
    `,
  Row: styled(Table.Row)`
    padding: 10px 15px;
    align-items:center;
    background-color: ${color('infoPanelHeaderBackground')};
    border-bottom: 1px solid ${color('customerPinUnpinned')};
    margin: 0 5px;
    &:last-child {
      border-bottom: none;
    }

    @media ${screenSize.small} {
      flex-flow: row wrap;
      padding: 10px 0 10px;
    }
  `,
  MessageCell: styled(Table.Cell).withConfig({
    shouldForwardProp: (prop) => prop !== 'flexValue'
  })`
    align-items: center;
    flex: ${({ flexValue }) => flexValue ?? MESSAGE_COLUMN_FLEX_VALUE};
    margin-left: 15px;
    @media ${screenSize.small} {
      flex: 1 1 100%;
      margin-right: 0;
      margin-bottom: 15px;
    }
  `,
  SiteMessage: styled(SiteMessage)`
    cursor: pointer;
  `,
  RolesAndBrands: styled.div`
    flex: 1;
    display: flex;
    gap: 8px;
    align-items: center;
 `,
  Role: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isFsr'
  })`
    font-style: italic;
    background-color: ${color('DashboardThermostatText')};
    color: ${color('newLabelText')};
    text-transform: ${({ isFsr }) => isFsr ? 'uppercase' : 'capitalize'};
    border-radius: 4px;
    padding: 4px 8px;
  `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'enabled'
  })`
    color: ${({ enabled }) =>
    enabled
      ? color('positiveStatus')
      : color('siteMessageStatusDisabledText')
};
  `,
  ProductLogo: styled.img`
    display: flex;
    height: 33px;
    justify-content: center;
  `,
  DateWrapper: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'publishedAt'
  })`
    display: flex;
    justify-content: ${({ publishedAt }) => publishedAt ? 'space-between' : 'flex-end'};
    flex: 1;
  `,
  Actions: styled.div`
    display: flex;
    gap: 8px;
  `,
  EditIcon: styled(EditIcon)`
    color: ${color('EditIcon')};
    width: 1.2em;
    height: 1.2em;
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
  ${iconStyle('actionableIcon')}
`,
  RestoreOutlinedIcon: styled(RestoreOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `,
  TableHeader: styled(Table.Header)`
    padding: 0px 5px;
  `
}
